"use client";

import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { 
  FileText, 
  CheckCircle, 
  Upload, 
  Pen, 
  Download,
  ArrowRight,
  RefreshCw,
  AlertTriangle,
  X
} from "lucide-react";
import openTurfLogo from '../assets/OpenTurf-logo.png';

interface AppointmentLetterProps {
  onComplete?: () => void;
  onShowTerms?: () => void;
  onBack?: () => void;
}

type SigningStep = "review" | "signing" | "success" | "error";

export function AppointmentLetter({ onComplete, onShowTerms, onBack }: AppointmentLetterProps) {
  const [currentStep, setCurrentStep] = React.useState<SigningStep>("review");
  const [hasAcceptedTerms, setHasAcceptedTerms] = React.useState(false);
  const [signatureType, setSignatureType] = React.useState<"draw" | "upload" | null>(null);
  const [signatureFile, setSignatureFile] = React.useState<File | null>(null);
  const [drawnSignature, setDrawnSignature] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // PDF content simulation
  const pdfContent = `
APPOINTMENT LETTER

OpenTurf Technologies Pvt. Ltd.
Bangalore, Karnataka, India

Date: ${new Date().toLocaleDateString('en-IN')}

Dear [Candidate Name],

We are pleased to offer you the position of [Job Title] at OpenTurf Technologies Pvt. Ltd.

TERMS AND CONDITIONS:

1. Position: You will be employed as [Job Title] in our [Department] department.

2. Start Date: Your employment will commence on [Start Date].

3. Compensation: Your annual salary will be INR [Amount] per annum, payable monthly.

4. Working Hours: Standard working hours are 9:00 AM to 6:00 PM, Monday through Friday.

5. Benefits: You will be entitled to health insurance, provident fund, and other benefits as per company policy.

6. Probation: You will be on probation for the first 6 months of employment.

7. Confidentiality: You agree to maintain confidentiality of all company information.

8. Termination: Either party may terminate employment with 30 days written notice.

Please sign and return this letter to confirm your acceptance of this offer.

We look forward to welcoming you to our team!

Sincerely,
HR Department
OpenTurf Technologies Pvt. Ltd.
  `;

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    setDrawnSignature(canvas.toDataURL());
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setDrawnSignature(null);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSignatureFile(file);
    }
  };

  const handleSubmit = async () => {
    if (!hasAcceptedTerms || (!drawnSignature && !signatureFile)) {
      return;
    }

    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIsSubmitting(false);
    
    // Simulate success/failure (90% success rate)
    const isSuccess = Math.random() > 0.1;
    setCurrentStep(isSuccess ? "success" : "error");
    
    if (isSuccess) {
      setTimeout(() => {
        onComplete?.();
      }, 3000);
    }
  };

  const canSubmit = hasAcceptedTerms && (drawnSignature || signatureFile);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    ctx.strokeStyle = '#1f2937';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  if (currentStep === "success") {
    return <SuccessScreen onShowTerms={onShowTerms} />;
  }

  if (currentStep === "error") {
    return (
      <ErrorScreen 
        onRetry={() => setCurrentStep("review")}
        onShowTerms={onShowTerms}
      />
    );
  }

  return (
    <div className="h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex flex-col relative">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-20 w-32 h-32 bg-blue-200/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-40 h-40 bg-purple-200/20 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-radial from-blue-100/10 to-transparent rounded-full" />
      </div>

      {/* Fixed Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 p-6 bg-white/50 backdrop-blur-sm border-b border-gray-200/50 flex-shrink-0"
      >
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <img 
            src={openTurfLogo} 
            alt="OpenTurf Technologies" 
            className="h-8 w-auto"
          />
          {onBack && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
            >
              Back to Demo
            </Button>
          )}
        </div>
      </motion.header>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-6xl mx-auto p-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col"
          >
            {/* Header Message */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl mx-auto flex items-center justify-center mb-6">
                <FileText className="w-8 h-8 text-white" />
              </div>
              
              <h1 className="text-gray-900 mb-4">
                Almost there! Please review and sign your appointment letter to complete onboarding.
              </h1>
              
              <p className="text-gray-600 max-w-2xl mx-auto">
                Take a moment to carefully review the terms of your employment. 
                Once you're ready, add your signature to make it official.
              </p>
            </div>

            {/* Main Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 min-h-[600px] lg:min-h-[700px]">
              {/* PDF Viewer */}
              <div className="lg:col-span-2">
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 }}
                  className="h-[600px] lg:h-[700px] bg-white/80 backdrop-blur-sm rounded-3xl shadow-lg border border-white/20 overflow-hidden flex flex-col"
                >
                  <div className="p-6 border-b border-gray-200/50 flex-shrink-0">
                    <div className="flex items-center justify-between">
                      <h3 className="text-gray-900">Appointment Letter</h3>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-gray-600 hover:text-gray-700"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download PDF
                      </Button>
                    </div>
                  </div>
                  
                  <div className="flex-1 overflow-y-auto p-6">
                    <div className="bg-white rounded-xl p-8 shadow-inner border">
                      <pre className="whitespace-pre-wrap text-gray-800 leading-relaxed">
                        {pdfContent}
                      </pre>
                      
                      {/* Signature Area in Document */}
                      <div className="mt-12 pt-8 border-t border-gray-300">
                        <div className="flex justify-between items-end">
                          <div>
                            <p className="text-gray-700 mb-4">Employee Signature:</p>
                            <div className="w-64 h-16 border-b-2 border-gray-400"></div>
                            <p className="text-sm text-gray-500 mt-2">Date: ________________</p>
                          </div>
                          <div>
                            <p className="text-gray-700 mb-4">HR Representative:</p>
                            <div className="w-64 h-16 border-b-2 border-gray-400"></div>
                            <p className="text-sm text-gray-500 mt-2">Date: {new Date().toLocaleDateString('en-IN')}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Signing Panel */}
              <div className="lg:col-span-1">
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 }}
                  className="h-[600px] lg:h-[700px] bg-white/80 backdrop-blur-sm rounded-3xl shadow-lg border border-white/20 p-6 flex flex-col overflow-y-auto"
                >
                  <h3 className="text-gray-900 mb-6">Sign Your Letter</h3>
                  
                  {/* Terms Confirmation */}
                  <div className="mb-6">
                    <div className="flex items-start space-x-3 p-4 bg-blue-50 rounded-xl border border-blue-200">
                      <Checkbox 
                        id="terms"
                        checked={hasAcceptedTerms}
                        onCheckedChange={(checked) => setHasAcceptedTerms(!!checked)}
                        className="mt-0.5"
                      />
                      <label 
                        htmlFor="terms" 
                        className="text-sm text-blue-800 leading-relaxed cursor-pointer"
                      >
                        I have read and agree to all terms and conditions outlined in this appointment letter.
                      </label>
                    </div>
                  </div>

                  {/* Signature Options */}
                  <div className="flex-1 space-y-4">
                    <h4 className="text-gray-900">Add Your Signature</h4>
                    
                    {/* Signature Type Selection */}
                    <div className="grid grid-cols-2 gap-3">
                      <Button
                        variant={signatureType === "draw" ? "default" : "outline"}
                        onClick={() => setSignatureType("draw")}
                        className="h-auto p-4 flex-col space-y-2"
                      >
                        <Pen className="w-5 h-5" />
                        <span className="text-xs">Draw</span>
                      </Button>
                      
                      <Button
                        variant={signatureType === "upload" ? "default" : "outline"}
                        onClick={() => setSignatureType("upload")}
                        className="h-auto p-4 flex-col space-y-2"
                      >
                        <Upload className="w-5 h-5" />
                        <span className="text-xs">Upload</span>
                      </Button>
                    </div>

                    {/* Draw Signature */}
                    {signatureType === "draw" && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-3"
                      >
                        <div className="border-2 border-dashed border-gray-300 rounded-xl p-2">
                          <canvas
                            ref={canvasRef}
                            width={240}
                            height={120}
                            className="w-full h-30 border rounded-lg bg-white cursor-crosshair"
                            onMouseDown={startDrawing}
                            onMouseMove={draw}
                            onMouseUp={stopDrawing}
                            onMouseLeave={stopDrawing}
                          />
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={clearSignature}
                          className="w-full"
                        >
                          Clear Signature
                        </Button>
                      </motion.div>
                    )}

                    {/* Upload Signature */}
                    {signatureType === "upload" && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-3"
                      >
                        <div
                          className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center cursor-pointer hover:border-gray-400 transition-colors"
                          onClick={() => fileInputRef.current?.click()}
                        >
                          {signatureFile ? (
                            <div className="space-y-2">
                              <CheckCircle className="w-8 h-8 text-green-600 mx-auto" />
                              <p className="text-sm text-gray-700">{signatureFile.name}</p>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSignatureFile(null);
                                }}
                                className="text-red-600 hover:text-red-700"
                              >
                                <X className="w-4 h-4 mr-1" />
                                Remove
                              </Button>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <Upload className="w-8 h-8 text-gray-400 mx-auto" />
                              <p className="text-sm text-gray-600">
                                Click to upload signature image
                              </p>
                              <p className="text-xs text-gray-500">
                                PNG, JPG up to 2MB
                              </p>
                            </div>
                          )}
                        </div>
                        
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleFileUpload}
                          className="hidden"
                        />
                      </motion.div>
                    )}
                  </div>

                  {/* Submit Button */}
                  <div className="pt-6 border-t border-gray-200">
                    <Button
                      onClick={handleSubmit}
                      disabled={!canSubmit || isSubmitting}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white disabled:bg-gray-300"
                    >
                      {isSubmitting ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Submit Signed Letter
                        </>
                      )}
                    </Button>
                    
                    {!canSubmit && (
                      <p className="text-xs text-gray-500 mt-2 text-center">
                        Please accept terms and add your signature
                      </p>
                    )}
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Footer */}
      <motion.footer 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
        className="relative z-10 p-6 border-t border-gray-200/50 bg-white/50 backdrop-blur-sm flex-shrink-0"
      >
        <div className="max-w-4xl mx-auto flex items-center justify-between w-full">
          <div className="flex items-center space-x-2">
            <img 
              src={openTurfLogo} 
              alt="OpenTurf Technologies" 
              className="h-6 w-auto opacity-60"
            />
            <span className="text-xs text-gray-500">
              © {new Date().getFullYear()} OpenTurf Technologies Pvt. Ltd.
            </span>
          </div>
          
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <span>Powered by TurfAI</span>
            <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
            <button 
              onClick={onShowTerms}
              className="text-purple-600 hover:text-purple-700 hover:underline transition-colors"
            >
              Terms & Conditions
            </button>
          </div>
        </div>
      </motion.footer>
    </div>
  );
}

// Success Screen Component
function SuccessScreen({ onShowTerms }: { onShowTerms?: () => void }) {
  return (
    <div className="h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex flex-col relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-20 w-32 h-32 bg-green-200/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-20 w-40 h-40 bg-blue-200/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-radial from-green-100/10 to-transparent rounded-full animate-pulse" />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="relative z-10 text-center max-w-md mx-auto"
        >
          {/* Success Icon with Animation */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.3, duration: 0.5, type: "spring", stiffness: 200 }}
            className="w-24 h-24 bg-gradient-to-r from-green-500 to-green-600 rounded-full mx-auto flex items-center justify-center mb-8 relative"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.8, duration: 0.3 }}
            >
              <CheckCircle className="w-12 h-12 text-white" />
            </motion.div>
            
            {/* Pulsing ring effect */}
            <motion.div
              className="absolute inset-0 rounded-full border-4 border-green-300"
              initial={{ scale: 1, opacity: 0.8 }}
              animate={{ scale: 1.5, opacity: 0 }}
              transition={{ delay: 1, duration: 1.5, repeat: Infinity, repeatDelay: 1 }}
            />
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="text-gray-900 mb-4"
          >
            Thanks! We've received your signed appointment letter. 🎉
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.6 }}
            className="text-gray-600 mb-8"
          >
            Your onboarding is now complete! Our HR team will be in touch with your first-day details shortly.
          </motion.p>

          {/* Success Details */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.6 }}
            className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-white/20 mb-8"
          >
            <h3 className="text-gray-900 mb-4">What's Next?</h3>
            <div className="text-left space-y-3 text-sm text-gray-600">
              <motion.div 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.1, duration: 0.4 }}
                className="flex items-start space-x-3"
              >
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                <p>You'll receive a welcome email with first-day instructions</p>
              </motion.div>
              <motion.div 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.3, duration: 0.4 }}
                className="flex items-start space-x-3"
              >
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                <p>HR will contact you within 24 hours with joining details</p>
              </motion.div>
              <motion.div 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.5, duration: 0.4 }}
                className="flex items-start space-x-3"
              >
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                <p>Keep an eye out for your access credentials and handbook</p>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Fixed Footer */}
      <motion.footer 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.7, duration: 0.6 }}
        className="relative z-10 p-6 border-t border-gray-200/50 bg-white/50 backdrop-blur-sm flex-shrink-0"
      >
        <div className="max-w-4xl mx-auto flex items-center justify-between w-full">
          <div className="flex items-center space-x-2">
            <img 
              src={openTurfLogo} 
              alt="OpenTurf Technologies" 
              className="h-6 w-auto opacity-60"
            />
            <span className="text-xs text-gray-500">
              © {new Date().getFullYear()} OpenTurf Technologies Pvt. Ltd.
            </span>
          </div>
          
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <span>Powered by TurfAI</span>
            <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
            <button 
              onClick={onShowTerms}
              className="text-purple-600 hover:text-purple-700 hover:underline transition-colors"
            >
              Terms & Conditions
            </button>
          </div>
        </div>
      </motion.footer>
    </div>
  );
}

// Error Screen Component
function ErrorScreen({ onRetry, onShowTerms }: { onRetry: () => void; onShowTerms?: () => void }) {
  return (
    <div className="h-screen bg-gradient-to-br from-red-50 via-white to-blue-50 flex flex-col relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-20 w-32 h-32 bg-red-200/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-40 h-40 bg-blue-200/20 rounded-full blur-3xl" />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="relative z-10 text-center max-w-md mx-auto"
        >
          <div className="w-20 h-20 bg-gradient-to-r from-red-500 to-red-600 rounded-full mx-auto flex items-center justify-center mb-6">
            <AlertTriangle className="w-10 h-10 text-white" />
          </div>
          
          <h1 className="text-gray-900 mb-4">
            Submission Failed 😔
          </h1>
          
          <p className="text-gray-600 mb-8">
            We couldn't process your signed letter right now. Please try submitting again.
          </p>

          <div className="space-y-4">
            <Button
              onClick={onRetry}
              className="bg-blue-600 hover:bg-blue-700 text-white w-full"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Try Again
            </Button>
          </div>
        </motion.div>
      </div>

      {/* Fixed Footer */}
      <motion.footer className="relative z-10 p-6 border-t border-gray-200/50 bg-white/50 backdrop-blur-sm flex-shrink-0">
        <div className="max-w-4xl mx-auto flex items-center justify-between w-full">
          <div className="flex items-center space-x-2">
            <img 
              src={openTurfLogo} 
              alt="OpenTurf Technologies" 
              className="h-6 w-auto opacity-60"
            />
            <span className="text-xs text-gray-500">
              © {new Date().getFullYear()} OpenTurf Technologies Pvt. Ltd.
            </span>
          </div>
          
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <span>Powered by TurfAI</span>
            <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
            <button 
              onClick={onShowTerms}
              className="text-purple-600 hover:text-purple-700 hover:underline transition-colors"
            >
              Terms & Conditions
            </button>
          </div>
        </div>
      </motion.footer>
    </div>
  );
}